<template>
  <div>
    <span class="profile-notifications">
      <v-menu
        v-model="notificationMenu"
        offset-y
        max-width="420"
        :close-on-content-click="false"
      >
        <template #activator="{ on, attrs }">
          <v-list-item
            v-if="$vuetify.breakpoint.mdAndDown"
            class="pb-1 pt-2"
            v-bind="attrs"
            v-on="on"
          >
            <NotificationBadge :notifications-length="total">
              <v-btn
                :color="total ? 'green darken-5' : 'grey lighten-2'"
                dark
                small
                fab
                :ripple="false"
              >
                <v-icon>mdi-bell</v-icon>
              </v-btn>
            </NotificationBadge>
            <span class="text--secondary ml-3">Notifications</span>
          </v-list-item>
          <NotificationBadge v-else :notifications-length="total">
            <v-btn
              :color="total ? 'green darken-5' : 'grey lighten-2'"
              dark
              small
              fab
              :ripple="false"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-bell</v-icon>
            </v-btn>
          </NotificationBadge>
        </template>
        <v-card
          class="mx-auto"
          max-width="400"
          tile
        >
          <v-subheader>
            <span class="mr-16 white-space--nowrap">Notifications ({{ total }})</span>
            <v-spacer />
            <v-btn
              v-if="total"
              x-small
              outlined
              class="ml-4"
              :loading="clearAllLoading"
              @click="clearAllNotifications"
            >
              Clear all
            </v-btn>
            <v-btn icon class="cancel" @click="notificationMenu = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-subheader>
          <v-divider />
          <v-list class="mb-0 pb-0 overflow-y-auto" style="max-height: 400px">
            <v-card-text v-if="!notifications.length && !loading" class="text--secondary">
              You're all caught up!
            </v-card-text>
            <div v-for="notification in notifications" :key="notification.id">
              <v-list-item
                :three-line="!!notification.body"
                :two-line="!notification.body"
                :disabled="notification.show"
                @click="openLink(notification)"
              >
                <v-progress-linear v-if="checkIsLoading(notification.id)" indeterminate color="primary" />
                <template v-else>
                  <v-list-item-content>
                    <v-list-item-title :class="{ 'primary--text': notification.metadata && !notification.show }" style="white-space: initial;">
                      {{ notification.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle
                      v-if="notification.body"
                      v-dompurify-html="notification.body"
                      style="white-space: initial;"
                      class="d-block"
                    />
                    <v-list-item-subtitle>
                      {{ notification.created_at | formatDate }} {{ notification.created_at | formatTime }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon small @click.stop="clearNotification(notification)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </template>
              </v-list-item>
              <v-divider />
            </div>
            <AppLoadMoreLoader
              v-if="hasMore"
              @end:intersect="$emit('load:more')"
            />
          </v-list>
        </v-card>
      </v-menu>
    </span>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { mapState } from 'vuex';
import mixins from 'vue-typed-mixins';

import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { INotification } from '@/bundles/Notification/interfaces/INotification';
import { notificationService } from '@/bundles/Notification/factories/notificationServiceFactory';

import NotificationBadge from '@/bundles/Panels/components/NotificationBadge.vue';
import AppLoadMoreLoader from '@/bundles/App/components/inputs/AppLoadMoreLoader.vue';

interface INotificationMenu {
  notificationMenu: boolean;
  loadingIds: number[];
  clearAllLoading: boolean;
}

export default mixins(AccountLayoutMixin).extend({
  name: 'NotificationMenu',

  components: { NotificationBadge, AppLoadMoreLoader },

  props: {
    notifications: {
      type: Array as PropType<INotification[]>,
      default: () => []
    },
    total: {
      type: Number,
      default: 0
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data: (): INotificationMenu => ({
    notificationMenu: false,
    loadingIds: [],
    clearAllLoading: false
  }),

  computed: {
    ...mapState({
      token: (state: any) => state.idToken
    }),
  },

  watch: {
    total (value) {
      if (!value) {
        this.notificationMenu = false;
      }
    }
  },

  methods: {
    openLink (notification: INotification) {
      if (notification.metadata) {
        window.open(notification.metadata.link, '_blank');
      }

      this.changeNotificationStatus(notification);
    },
    async clearNotification (notification: INotification) {
      try {
        this.loadingIds.push(notification.id);
        await notificationService.updateNotificationStatus(notification.id);
        this.$emit('clearNotification', notification.id);
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.loadingIds = this.loadingIds.filter(id => id !== notification.id);
      }
    },
    async changeNotificationStatus (notification: INotification) {
      try {
        this.loadingIds.push(notification.id);
        await notificationService.updateNotificationStatus(notification.id);

        this.$emit('updateNotification', { ...notification, show: true });
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
      } finally {
        this.loadingIds = this.loadingIds.filter(id => id !== notification.id);
      }
    },
    checkIsLoading (notificationId: number) {
      return this.loadingIds.some(id => notificationId === id);
    },
    async clearAllNotifications () {
      try {
        this.clearAllLoading = true;
        await notificationService.clearAllNotifications();

        this.$emit('clearAllNotifications');
      } catch (error: any) {
        this.addNotification({ ...error });
      } finally {
        this.clearAllLoading = false;
      }
    }
  }
})
</script>
